'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import { ComponentProps, ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';
import { AppConfigContext } from '@/features/app-providers/app-config-provider';
import { resolveLegalModalContent } from '@/features/legal/lib/resolve-legal-modal-content';
import { QueryApi } from '@/features/query/api';
import { Language } from '@/shared/model/Language';
import { Button } from '@/shared/ui/button';
import { ClientMDXRenderer } from '@/shared/ui/client-mdx-renderer';
import { Layout } from '@/shared/ui/layout';
import { Modal } from '@/shared/ui/modal';
import { ModalProps } from '@/shared/ui/modal/types';
import './register-terms-and-conditions-modal.scss';

type Props = ModalProps & {
    onAccept?: () => void;
    children?: ReactNode;
};

type RegisterTermsAndConditionsModalContextValue = {
    onAccept?: () => void;
    onClose: ModalProps['onClose'];
    venueContent: string;
    globalContent: string;
    isButtonAcceptEnabled: boolean;
    scrollContainerRef: React.RefObject<HTMLDivElement | null>;
    handleScroll: (e: React.UIEvent<HTMLElement>) => void;
};

const RegisterTermsAndConditionsModalContext =
    createContext<RegisterTermsAndConditionsModalContextValue | null>(null);

const useRegisterTermsAndConditionsModalContext = () => {
    const context = useContext(RegisterTermsAndConditionsModalContext);
    if (!context) {
        throw new Error(
            'RegisterTermsAndConditionsModal components must be used within RegisterTermsAndConditionsModalWrapper.',
        );
    }
    return context;
};

const useRegisterTermsAndConditionsModalState = (onAccept?: () => void, onClose?: Props['onClose']) => {
    const t = useTranslations();
    const locale = useLocale().toUpperCase() as Language;
    const { termsAndConditions } = useContext(AppConfigContext);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const [isButtonAcceptEnabled, setIsButtonAcceptEnabled] = useState(false);
    const globalTermsAndConditionsQuery = useQuery(QueryApi.CMS.queries.TermsAndConditions(locale));

    const handleScrollPrivacyPolicy = (e: React.UIEvent<HTMLElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

        if (scrollTop + clientHeight >= scrollHeight - 1) {
            setIsButtonAcceptEnabled(true);
        } else {
            setIsButtonAcceptEnabled(false);
        }
    };

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }

        const { scrollHeight, clientHeight } = container;

        if (scrollHeight <= clientHeight) {
            setIsButtonAcceptEnabled(true);
        }
    }, []);

    return {
        contextValue: {
            onAccept,
            onClose: onClose!,
            venueContent: resolveLegalModalContent(termsAndConditions, t('LEGAL_CONTENT.PREPARING')),
            globalContent: globalTermsAndConditionsQuery.data?.translation.content || '',
            isButtonAcceptEnabled,
            scrollContainerRef,
            handleScroll: handleScrollPrivacyPolicy,
        },
        scrollContainerRef,
        handleScrollPrivacyPolicy,
    };
};

export const RegisterTermsAndConditionsModalWrapper = ({ onAccept, children, ...props }: Props) => {
    const { contextValue, scrollContainerRef, handleScrollPrivacyPolicy } =
        useRegisterTermsAndConditionsModalState(onAccept, props.onClose);

    return (
        <RegisterTermsAndConditionsModalContext.Provider value={contextValue}>
            <Modal
                {...props}
                wrapperProps={{
                    className: 'register-terms-and-conditions-modal',
                    containerProps: {
                        className: 'register-terms-and-conditions-modal__container',
                    },
                    'data-testid': 'register-terms-and-conditions-modal',
                    scrollerProps: {
                        ref: scrollContainerRef,
                        onScroll: handleScrollPrivacyPolicy,
                    },
                }}
            >
                {children}
            </Modal>
        </RegisterTermsAndConditionsModalContext.Provider>
    );
};

/** Provider that only provides context and renders children (Modal prefab). Renders Modal with scrollerProps for scroll-to-enable. */
export const RegisterTermsAndConditionsModalProvider = ({ onAccept, children, ...props }: Props) => {
    const { contextValue, scrollContainerRef, handleScrollPrivacyPolicy } =
        useRegisterTermsAndConditionsModalState(onAccept, props.onClose);

    return (
        <RegisterTermsAndConditionsModalContext.Provider value={contextValue}>
            <Modal
                {...props}
                wrapperProps={{
                    className: 'register-terms-and-conditions-modal',
                    containerProps: {
                        className: 'register-terms-and-conditions-modal__container',
                    },
                    'data-testid': 'register-terms-and-conditions-modal',
                    scrollerProps: {
                        ref: scrollContainerRef,
                        onScroll: handleScrollPrivacyPolicy,
                    },
                }}
            >
                {children}
            </Modal>
        </RegisterTermsAndConditionsModalContext.Provider>
    );
};

/** Renders only the title text. Use inside prefab: "Modal.Title". */
const RegisterTermsAndConditionsModalTitle = () => {
    const t = useTranslations();
    return <>{t('TERMS_AND_CONDITIONS_MODAL.TITLE')}</>;
};

type ContentProps = ComponentProps<typeof Layout>;
const RegisterTermsAndConditionsModalContent = ({ media, ...props }: ContentProps) => {
    const { venueContent, globalContent } = useRegisterTermsAndConditionsModalContext();
    return (
        <Layout media={media} data-testid="register-terms-and-conditions-modal-content" {...props}>
            <ClientMDXRenderer content={venueContent} />
            <ClientMDXRenderer content={globalContent} />
        </Layout>
    );
};

type AcceptButtonProps = ComponentProps<typeof Button>;
const RegisterTermsAndConditionsModalAcceptButton = ({ onClick, ...props }: AcceptButtonProps) => {
    const t = useTranslations();
    const { onAccept, onClose, isButtonAcceptEnabled } = useRegisterTermsAndConditionsModalContext();

    if (!onAccept) {
        return null;
    }

    return (
        <Layout
            element={Button}
            data-testid="register-terms-and-conditions-modal-accept-button"
            disabled={!isButtonAcceptEnabled}
            onClick={(event) => {
                onClick?.(event);
                onAccept?.();
                onClose();
            }}
            {...props}
        >
            {t('TERMS_AND_CONDITIONS_MODAL.ACCEPT_BUTTON')}
        </Layout>
    );
};

export { RegisterTermsAndConditionsModalWrapper as RegisterTermsAndConditionsModal };

export {
    RegisterTermsAndConditionsModalWrapper as Wrapper,
    RegisterTermsAndConditionsModalProvider as Provider,
    RegisterTermsAndConditionsModalTitle as Title,
    RegisterTermsAndConditionsModalContent as Content,
    RegisterTermsAndConditionsModalAcceptButton as AcceptButton,
};
